import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Phone,
  Truck,
  Tags,
  Clock,
  MonitorSmartphone,
  Crosshair,
  BadgeCheck,
  CheckCircle2,
  Building2,
  Car,
  Wrench,
  Briefcase,
  CalendarCheck,
  Receipt,
  LineChart,
} from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import QuoteRequestModal from '../components/site/QuoteRequestModal';
import { useSeo, useJsonLd, SITE_URL } from '../lib/seo';

const FLEET_IMAGE =
  'https://customer-assets-lxgj4vgw.emergentagent.net/job_glass-quote-hub-1/artifacts/df55f9bc7c4561c0_image.png';
const PORTAL_URL = 'https://glassflow.ca/portal/a-auto-glass--sipm';

const BENEFITS = [
  {
    icon: Tags,
    title: 'Competitive Wholesale Pricing',
    desc: 'Volume pricing built for dealerships and fleets, with the same rate on every unit so your cost per job is predictable month after month.',
  },
  {
    icon: Clock,
    title: 'Fast Turnaround',
    desc: 'Same-day and next-day appointments on most vehicles. We know a car sitting on your lot with damaged glass is a car you cannot sell or rent.',
  },
  {
    icon: MonitorSmartphone,
    title: 'Online Booking Portal',
    desc: 'See your pricing, book the work and follow every job from your desk — no phone tag, no waiting on a quote by email.',
  },
  {
    icon: Truck,
    title: 'Mobile Fleet To Your Lot',
    desc: 'Our mobile units come to your dealership, yard or job site fully equipped, so your vehicles never leave your property.',
  },
  {
    icon: Crosshair,
    title: 'In-House ADAS Calibration',
    desc: 'Static and dynamic calibration completed with the glass replacement, with the scan-tool report filed to the vehicle record.',
  },
  {
    icon: BadgeCheck,
    title: 'Lifetime Workmanship Warranty',
    desc: 'Every installation is backed against leaks and workmanship defects, so the vehicle you hand over is one you can stand behind.',
  },
];

const PORTAL_FEATURES = [
  { icon: Tags, label: 'See your account pricing before you commit — by year, make and model.' },
  { icon: CalendarCheck, label: 'Book the work yourself, choose mobile or in-shop, and pick the day.' },
  { icon: LineChart, label: 'Track every job from booked to completed, with calibration status.' },
  { icon: Receipt, label: 'Pull invoices and job history for any unit in your inventory.' },
];

const SEGMENTS = [
  { icon: Building2, title: 'New & Used Dealerships', desc: 'Reconditioning glass on trade-ins and lot inventory, ready for the front line fast.' },
  { icon: Car, title: 'Leasing & Rental Fleets', desc: 'Keep units earning — chips repaired and windshields replaced between rentals.' },
  { icon: Briefcase, title: 'Commercial & Municipal Fleets', desc: 'Service trucks, vans and work vehicles repaired on site so crews stay productive.' },
  { icon: Wrench, title: 'Body Shops & Garages', desc: 'Trade pricing on glass and ADAS calibration you can sublet with confidence.' },
];

function useOnScreen(ref) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
  return visible;
}

function Reveal({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const visible = useOnScreen(ref);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const dealersSchema = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dealership & Fleet Auto Glass Services',
  serviceType: 'Wholesale auto glass replacement and ADAS calibration for dealerships and fleets',
  description:
    'Wholesale auto glass for Ottawa dealerships, leasing and rental fleets, commercial fleets and body shops: competitive volume pricing, same/next-day mobile service, in-house ADAS calibration and an online portal for pricing and booking.',
  url: `${SITE_URL}/dealers`,
  provider: { '@type': 'AutoRepair', name: 'A+ Auto Glass', telephone: '+1-343-558-8820' },
  areaServed: ['Ottawa', 'Orléans', 'Kanata', 'Gatineau', 'Nepean', 'Barrhaven'],
};

export default function Dealers() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  useSeo({
    title: 'Dealership & Fleet Auto Glass in Ottawa — Wholesale Pricing & Online Booking | A+ Auto Glass',
    description:
      'Auto glass for Ottawa dealerships and fleets: competitive wholesale pricing, same/next-day mobile service across the region, in-house ADAS calibration and an online portal to see pricing and book the work. Call 343-558-8820.',
    path: '/dealers',
  });
  useJsonLd('ld-dealers', dealersSchema);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    const handler = () => setQuoteOpen(true);
    window.addEventListener('open-quote-modal', handler);
    return () => window.removeEventListener('open-quote-modal', handler);
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#111827]" data-testid="dealers-page">
      <Navbar />

      {/* HERO — matches Services / Why Choose Us / Locations / Contact */}
      <section className="relative overflow-hidden bg-[#0a1220] text-white">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[#0D5BD7]/20 to-transparent pointer-events-none" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-12 md:py-14">
          <h1 className="font-black leading-[1.05] tracking-[-0.015em] text-[32px] md:text-[42px] lg:text-[48px] max-w-[680px]">
            Dealerships &amp; Fleets
          </h1>
          <p className="mt-4 text-[15px] md:text-[16.5px] text-white/85 max-w-[600px] leading-[1.65]">
            Competitive wholesale pricing, fast turnaround and an online portal where you can see
            your pricing and book the work yourself.
          </p>
        </div>
      </section>

      {/* INTRO + FLEET PHOTO */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-5">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">OUR MOBILE FLEET</div>
            <h2 className="mt-4 text-[28px] md:text-[38px] font-black tracking-[-0.01em] leading-[1.1]">
              Your vehicles never have to leave the lot.
            </h2>
            <p className="mt-5 text-[16px] text-[#5F6773] leading-[1.8]">
              A+ Auto Glass runs a fleet of fully equipped mobile units out of our Orléans shop. We
              carry glass, urethane, calibration equipment and trim clips with us, so a technician
              can complete a replacement and recalibration right in your service lane or parking lot.
            </p>
            <p className="mt-4 text-[16px] text-[#5F6773] leading-[1.8]">
              Multiple units on the road means we can take several of your vehicles in a single day
              and keep your inventory moving.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <button
                onClick={() => setQuoteOpen(true)}
                data-testid="dealers-intro-quote-btn"
                className="group inline-flex items-center justify-center min-h-[52px] px-7 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[11.5px] font-bold tracking-[0.22em] transition-all shadow-sm hover:shadow-md hover:-translate-y-[1px] active:scale-[0.98]"
              >
                BECOME A PARTNER
                <ArrowRight className="w-3.5 h-3.5 ml-3 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="tel:3435588820"
                data-testid="dealers-call-link"
                className="inline-flex items-center justify-center gap-3 min-h-[52px] px-7 rounded-full border-2 border-[#0D5BD7] text-[#0D5BD7] hover:bg-[#0D5BD7] hover:text-white text-[11.5px] font-bold tracking-[0.22em] transition-colors active:scale-[0.98]"
              >
                <Phone className="w-4 h-4" />
                343.558.8820
              </a>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-7" delay={120}>
            <div className="rounded-2xl overflow-hidden shadow-[0_18px_50px_rgba(15,23,42,0.14)]">
              <img
                src={FLEET_IMAGE}
                alt="A+ Auto Glass mobile service fleet parked at the Orléans shop in Ottawa"
                className="w-full object-cover aspect-[4/3] lg:aspect-[3/2]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-[#F8F9FB] py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-[720px]">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">WHY PARTNER WITH US</div>
            <h2 className="mt-4 text-[30px] md:text-[42px] font-black tracking-[-0.01em] leading-[1.08]">
              Built around how your business actually runs.
            </h2>
            <p className="mt-5 text-[16px] text-[#5F6773] leading-[1.75]">
              Clear pricing, quick scheduling and one local team accountable for the work — from a
              single trade-in to a full fleet rotation.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-7">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 70}>
                <div
                  data-testid={`dealer-benefit-${i}`}
                  className="group h-full bg-white rounded-2xl border border-slate-100 p-8 shadow-[0_2px_16px_rgba(15,23,42,0.05)] hover:shadow-[0_20px_44px_rgba(13,91,215,0.12)] hover:-translate-y-1 transition-all duration-300"
                >
                  <span className="w-14 h-14 rounded-2xl bg-[#0D5BD7]/10 text-[#0D5BD7] flex items-center justify-center group-hover:bg-[#0D5BD7] group-hover:text-white transition-colors">
                    <b.icon className="w-6 h-6" strokeWidth={1.7} />
                  </span>
                  <h3 className="mt-6 text-[17px] font-black tracking-tight leading-snug">{b.title}</h3>
                  <p className="mt-3 text-[14.5px] text-[#5F6773] leading-[1.75]">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PORTAL */}
      <section className="bg-[#0a1220] text-white py-16 md:py-24" data-testid="dealers-portal">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#7fb0ff]">PARTNER PORTAL</div>
            <h2 className="mt-4 text-[30px] md:text-[40px] font-black tracking-[-0.01em] leading-[1.1]">
              See your pricing. Book the work. Any time.
            </h2>
            <p className="mt-5 text-[16px] text-white/80 leading-[1.8]">
              Approved partners get a portal login, so your team can quote a vehicle and schedule the
              job in under a minute — whether that is at 9am with a service advisor on the line or at
              11pm while you plan tomorrow&rsquo;s lot work.
            </p>
            <ul className="mt-8 space-y-4">
              {PORTAL_FEATURES.map((f) => (
                <li key={f.label} className="flex items-start gap-4 text-[15px] text-white/90 leading-[1.6]">
                  <span className="mt-0.5 w-9 h-9 rounded-xl bg-white/10 text-[#7fb0ff] flex items-center justify-center shrink-0">
                    <f.icon className="w-4 h-4" strokeWidth={1.8} />
                  </span>
                  <span>{f.label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <a
                href={PORTAL_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="dealers-portal-link"
                className="group inline-flex items-center justify-center min-h-[52px] px-7 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[11.5px] font-bold tracking-[0.22em] transition-all shadow-[0_12px_30px_rgba(13,91,215,0.4)] hover:-translate-y-[2px]"
              >
                OPEN THE PORTAL
                <ArrowRight className="w-3.5 h-3.5 ml-3 transition-transform group-hover:translate-x-1" />
              </a>
              <Link
                to="/contact"
                data-testid="dealers-portal-access-link"
                className="inline-flex items-center justify-center min-h-[52px] px-7 rounded-full border-2 border-white/40 text-white text-[11.5px] font-bold tracking-[0.2em] hover:bg-white hover:text-[#111827] transition-colors"
              >
                REQUEST ACCESS
              </Link>
            </div>
          </Reveal>

          <Reveal className="lg:col-span-6" delay={120}>
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-8 md:p-10 backdrop-blur-sm">
              <div className="text-[11px] font-bold tracking-[0.24em] text-[#7fb0ff]">HOW IT WORKS</div>
              <ol className="mt-6 space-y-6">
                {[
                  ['Send us your details', 'Tell us your business name, volume and who should have access.'],
                  ['We set up your account', 'Your negotiated pricing is loaded and your team gets a login.'],
                  ['Quote and book in minutes', 'Enter the vehicle, see the price, pick mobile or in-shop, done.'],
                ].map(([t, d], i) => (
                  <li key={t} className="flex items-start gap-5">
                    <span className="w-10 h-10 rounded-full bg-[#0D5BD7] text-white font-black text-[15px] flex items-center justify-center shrink-0">
                      {i + 1}
                    </span>
                    <div>
                      <div className="text-[16px] font-bold">{t}</div>
                      <div className="mt-1.5 text-[14.5px] text-white/70 leading-[1.7]">{d}</div>
                    </div>
                  </li>
                ))}
              </ol>
              <div className="mt-8 pt-7 border-t border-white/10 flex items-start gap-3 text-[14px] text-white/70 leading-[1.7]">
                <CheckCircle2 className="w-5 h-5 text-[#7fb0ff] shrink-0 mt-0.5" strokeWidth={1.8} />
                No setup fee and no minimum volume — accounts are open to dealerships, fleets and
                trade partners across the Ottawa region.
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-[720px]">
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#0D5BD7]">WHO WE WORK WITH</div>
            <h2 className="mt-4 text-[30px] md:text-[42px] font-black tracking-[-0.01em] leading-[1.08]">
              Trusted by Ottawa&rsquo;s trade.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {SEGMENTS.map((s, i) => (
              <Reveal key={s.title} delay={i * 70}>
                <div
                  data-testid={`dealer-segment-${i}`}
                  className="h-full rounded-2xl border border-slate-100 bg-[#F8F9FB] p-7 hover:bg-white hover:border-[#0D5BD7]/30 hover:shadow-[0_16px_38px_rgba(15,23,42,0.08)] transition-all duration-300"
                >
                  <s.icon className="w-7 h-7 text-[#0D5BD7]" strokeWidth={1.7} />
                  <h3 className="mt-5 text-[16px] font-black tracking-tight leading-snug">{s.title}</h3>
                  <p className="mt-2.5 text-[14px] text-[#5F6773] leading-[1.7]">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#111827] text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 md:py-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-bold tracking-[0.28em] text-[#4a8cff]">LET&rsquo;S TALK VOLUME</div>
            <h2 className="mt-3 text-[28px] md:text-[36px] font-black leading-[1.1] tracking-[-0.01em]">
              Get your dealership pricing.
            </h2>
            <p className="mt-3 text-[14.5px] text-[#c3c9d4] leading-[1.7] max-w-xl">
              Send us a note about your business and we&rsquo;ll come back with your account pricing
              and portal access — usually within the same business day.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <button
              onClick={() => setQuoteOpen(true)}
              data-testid="dealers-cta-quote-btn"
              className="group inline-flex items-center justify-center h-14 px-8 rounded-full bg-[#0D5BD7] hover:bg-[#0a4bb5] text-white text-[12px] font-bold tracking-[0.22em] transition-all shadow-md hover:shadow-lg hover:-translate-y-[1px] active:scale-[0.98]"
            >
              BECOME A PARTNER
              <ArrowRight className="w-4 h-4 ml-4 transition-transform group-hover:translate-x-1" />
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center h-14 px-8 rounded-full border-2 border-white/40 text-white text-[12px] font-bold tracking-[0.2em] hover:bg-white hover:text-[#111827] transition-colors"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <QuoteRequestModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </div>
  );
}
